import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { default as Button, default as ButtonComponent } from '../../components/FormComponents/Button';
import { IN_PROGRESS, PUBLISHED, UPDATE_SURVEY_SERIES } from '../../constants';
import { State } from '../../models/state';
import { cloneSurvey, getSurveySeriesById } from '../../store/actions/survey';
import Confirmation from '../Confirmation/Confirmation';
import Series from '../SurveyForm/Series';
import Table from '../Table/Table';
import ToastMessage from '../ToastMessage/ToastMessage';

function SurveySeriesEdit() {
  let columns,
    surveyData = [];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { seriesId } = useParams();
  const { surveySeries } = useSelector((state: State) => state.survey);
  const { isAdmin } = useSelector((state: State) => state.auth.user);
  const [seriesPopup, setSeriesPopup] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmationPopupText, setConfirmationPopupText] = useState('');
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [surveyToClone, setSurveyToClone] = useState<{
    id: string;
    name: string;
  }>();
  const [pageLoading, setPageLoading] = useState(false);

  const getData = () => {
    setPageLoading(true);
    dispatch(getSurveySeriesById(seriesId, () => setPageLoading(false)));
  };

  useEffect(() => {
    getData();
  }, []);

  const goToCreateSurvey = () => {
    navigate(`/survey-series/${seriesId}/survey/create`);
  };

  const closeConfirmationPopup = () => {
    setConfirmationPopupText('');
    setSurveyToClone(null);
  };

  const clone = () => {
    setConfirmLoading(true);
    dispatch(
      cloneSurvey(surveyToClone?.id as string, (res, message) => {
        setConfirmLoading(false);
        closeConfirmationPopup();
        if (res) {
          setToastType('success');
          setToast('Survey is cloned');
          getData();
          window?.scrollTo({
            behavior: 'smooth',
            top: 0,
          });
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  if (isAdmin) {
    columns = ['Name', 'Status'];
    surveyData = surveySeries?.surveys || [];
  } else {
    columns = ['Name', 'Progress', 'Open for submissions', 'Notes'];
    surveyData = (surveySeries?.surveys || []).map(survey => {
      if (survey?.organizationSurveys?.[0]?.progress) {
        survey.progress = survey?.organizationSurveys?.[0]?.progress;
        if (survey.progress === IN_PROGRESS && survey.progressPercentage) {
          survey.progress = `${survey.progress} ${survey.progressPercentage}%`;
        }
      }
      survey['openForSubmissions'] = survey.status === PUBLISHED ? 'Yes' : 'No';
      return survey;
    });
  }

  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <Box className='survey-series'>
      <Box className='survey-series-header'>
        <Box>
          <Typography variant='h5'>Survey series</Typography>
          <Box display='flex' alignItems='baseline' gap={2} marginTop={2}>
            {surveySeries.name}
            {isAdmin && <ButtonComponent text='Edit' onClick={() => setSeriesPopup(true)} size='small' />}
          </Box>
        </Box>
        {isAdmin && (
          <Box className='create-button'>
            <Button text='Create survey' onClick={goToCreateSurvey} />
          </Box>
        )}
      </Box>
      <Box sx={{ ml: 2, mt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant='body1' display='inline'>
            Surveys
          </Typography>
        </Box>
        <Table
          columns={columns}
          rows={surveyData}
          page={0}
          rowClickHandler={(id: string) => navigate(`/survey-series/${seriesId}/survey/${id}`)}
          actionIcons={
            isAdmin
              ? [
                  {
                    title: 'Clone',
                    icon: <FileCopyIcon key='clone' htmlColor='#848884' />,
                    onClick: id => {
                      const survey = surveyData.find(item => item.id === id);
                      setSurveyToClone(survey);
                      setConfirmationPopupText(
                        `Are you sure you want to clone the survey ${survey.name ? `'${survey.name}'` : ''} ?`,
                      );
                    },
                  },
                ]
              : []
          }
        />
      </Box>
      <Series
        open={seriesPopup}
        closePopup={() => {
          setSeriesPopup(false);
        }}
        getData={getData}
        type={UPDATE_SURVEY_SERIES}
        seriesId={seriesId}
        name={surveySeries.name}
      />
      <Confirmation
        confirm={clone}
        text={confirmationPopupText}
        close={closeConfirmationPopup}
        loading={confirmLoading}
      />
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType || 'success'} />
    </Box>
  );
}

export default SurveySeriesEdit;
